import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, TOKEN_KEY, formatApiError } from "./api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [state, setState] = useState({
        status: "loading", // loading | authed | anon
        user: null,
        organisation: null,
    });

    const applyAccent = useCallback((org) => {
        if (org?.branding?.accent_color) {
            document.documentElement.style.setProperty(
                "--accent-color",
                org.branding.accent_color,
            );
        }
    }, []);

    const refresh = useCallback(async () => {
        const token = localStorage.getItem(TOKEN_KEY);
        if (!token) {
            setState({ status: "anon", user: null, organisation: null });
            return;
        }
        try {
            const { data } = await api.get("/auth/me");
            applyAccent(data.organisation);
            setState({ status: "authed", user: data.user, organisation: data.organisation });
        } catch (e) {
            localStorage.removeItem(TOKEN_KEY);
            setState({ status: "anon", user: null, organisation: null });
        }
    }, [applyAccent]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const login = async (email, password) => {
        try {
            const { data } = await api.post("/auth/login", { email, password });
            localStorage.setItem(TOKEN_KEY, data.access_token);
            applyAccent(data.organisation);
            setState({ status: "authed", user: data.user, organisation: data.organisation });
            return { ok: true };
        } catch (e) {
            return { ok: false, error: formatApiError(e) };
        }
    };

    const logout = async () => {
        try {
            await api.post("/auth/logout");
        } catch (e) {
            /* ignore */
        }
        localStorage.removeItem(TOKEN_KEY);
        setState({ status: "anon", user: null, organisation: null });
    };

    const setOrganisation = (org) => {
        applyAccent(org);
        setState((s) => ({ ...s, organisation: org }));
    };

    return (
        <AuthContext.Provider value={{ ...state, login, logout, refresh, setOrganisation }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be inside AuthProvider");
    return ctx;
}
